/**
 * WorksScheduleSection - Previsionnel travaux.
 *
 * Affiche la timeline verticale combinant :
 *  - les delais marques include_in_works_schedule=True
 *  - les items standalone ajoutes manuellement (source: 'custom')
 *
 * Reutilise par :
 *   - ProjectDetailPage  (mode 'internal' : statuts complets, CRUD items standalone)
 *   - ClientPortal        (mode 'client'   : statuts simplifies Planifie/En cours/Termine)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  planifie:  { label: 'Planifie',  bg: 'bg-gray-100',    text: 'text-gray-700',    dot: 'bg-gray-400'    },
  en_cours:  { label: 'En cours',  bg: 'bg-amber-100',   text: 'text-amber-800',   dot: 'bg-amber-500'   },
  termine:   { label: 'Termine',   bg: 'bg-green-100',   text: 'text-green-800',   dot: 'bg-green-500'   },
};

const ALERT_META = {
  red:    { label: 'En retard',     bg: 'bg-red-100',     text: 'text-red-700',    dot: 'bg-red-500',    ring: 'ring-red-200' },
  yellow: { label: 'Dans 7 jours',  bg: 'bg-amber-100',   text: 'text-amber-700',  dot: 'bg-amber-500',  ring: 'ring-amber-200' },
  green:  { label: 'A venir',       bg: 'bg-green-100',   text: 'text-green-700',  dot: 'bg-green-500',  ring: 'ring-green-200' },
  gray:   { label: 'Sans date',     bg: 'bg-gray-100',    text: 'text-gray-600',   dot: 'bg-gray-400',   ring: 'ring-gray-200' },
};

const STATUS_OPTIONS = [
  { value: 'planifie', label: 'Planifie' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'termine',  label: 'Termine' },
];

/**
 * Iter 92 (Fev 2026) : Vue calendrier laterale style Google Agenda.
 * Chaque intervention est tracee comme une barre coloree etalee sur sa duree
 * (start_date -> end_date). Une couleur stable est assignee par intervention.
 * Cliquer sur une barre = scroll vers la carte de la timeline.
 */
const BAR_PALETTE = [
  { bar: 'bg-blue-500',    soft: 'bg-blue-100',    text: 'text-blue-700'    },
  { bar: 'bg-purple-500',  soft: 'bg-purple-100',  text: 'text-purple-700'  },
  { bar: 'bg-pink-500',    soft: 'bg-pink-100',    text: 'text-pink-700'    },
  { bar: 'bg-emerald-500', soft: 'bg-emerald-100', text: 'text-emerald-700' },
  { bar: 'bg-amber-500',   soft: 'bg-amber-100',   text: 'text-amber-700'   },
  { bar: 'bg-rose-500',    soft: 'bg-rose-100',    text: 'text-rose-700'    },
  { bar: 'bg-cyan-600',    soft: 'bg-cyan-100',    text: 'text-cyan-700'    },
  { bar: 'bg-indigo-500',  soft: 'bg-indigo-100',  text: 'text-indigo-700'  },
  { bar: 'bg-teal-500',    soft: 'bg-teal-100',    text: 'text-teal-700'    },
  { bar: 'bg-orange-500',  soft: 'bg-orange-100',  text: 'text-orange-700'  },
];

const ScheduleSideCalendar = ({ items }) => {
  const [monthOffset, setMonthOffset] = React.useState(0);
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = base.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const dow = (base.getDay() + 6) % 7;
  const start = new Date(base);
  start.setDate(base.getDate() - dow);
  start.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString().slice(0, 10);

  // Couleur stable par intervention (basee sur son index dans la liste)
  const colorFor = (item) => {
    const list = items || [];
    const idx = list.findIndex((x) => x.item_id === item.item_id);
    return BAR_PALETTE[((idx >= 0 ? idx : 0)) % BAR_PALETTE.length];
  };

  // Genere 6 semaines (42 jours)
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(start);
      day.setDate(start.getDate() + w * 7 + d);
      week.push(day);
    }
    weeks.push(week);
  }

  // Pour chaque semaine, calcule les barres avec allocation greedy en lignes
  const weekBars = weeks.map((week) => {
    const weekStart = new Date(week[0]); weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(week[6]); weekEnd.setHours(0, 0, 0, 0);
    const bars = [];
    (items || []).forEach((it) => {
      const sd = (it.start_date || it.target_date || '').slice(0, 10);
      const ed = (it.end_date || sd || '').slice(0, 10);
      if (!sd) return;
      const sDate = new Date(sd); sDate.setHours(0, 0, 0, 0);
      const eDate = new Date(ed); eDate.setHours(0, 0, 0, 0);
      if (eDate < weekStart || sDate > weekEnd) return;
      const segStart = sDate < weekStart ? weekStart : sDate;
      const segEnd = eDate > weekEnd ? weekEnd : eDate;
      const startCol = Math.round((segStart - weekStart) / 86400000);
      const span = Math.round((segEnd - segStart) / 86400000) + 1;
      const startsHere = sDate >= weekStart;
      bars.push({ startCol, span, item: it, startsHere });
    });
    bars.sort((a, b) => a.startCol - b.startCol);
    const rows = [];
    bars.forEach((bar) => {
      let placed = false;
      for (let r = 0; r < rows.length; r++) {
        const last = rows[r][rows[r].length - 1];
        if (last.startCol + last.span <= bar.startCol) {
          rows[r].push(bar);
          bar.row = r;
          placed = true;
          break;
        }
      }
      if (!placed) { bar.row = rows.length; rows.push([bar]); }
    });
    return { bars, rowCount: rows.length };
  });

  const onBarClick = (item) => {
    const el = document.querySelector(`[data-testid="works-schedule-item-${item.item_id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const ROW_H = 16; // px - hauteur d'une barre

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="works-schedule-side-calendar">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold text-gray-800 capitalize">{monthLabel}</h4>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonthOffset((o) => o - 1)} className="px-2 py-0.5 text-xs rounded hover:bg-gray-100" data-testid="mini-cal-prev">‹</button>
          <button onClick={() => setMonthOffset(0)} className="px-2 py-0.5 text-xs rounded hover:bg-gray-100" data-testid="mini-cal-today">Auj.</button>
          <button onClick={() => setMonthOffset((o) => o + 1)} className="px-2 py-0.5 text-xs rounded hover:bg-gray-100" data-testid="mini-cal-next">›</button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-[9px] uppercase text-gray-400 mb-1 text-center font-semibold">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div>
        {weeks.map((week, wIdx) => {
          const { bars, rowCount } = weekBars[wIdx];
          const minBodyH = Math.max(rowCount, 1) * ROW_H + 4;
          return (
            <div key={wIdx} className="border-t border-gray-100">
              {/* Numeros des jours */}
              <div className="grid grid-cols-7">
                {week.map((d, i) => {
                  const iso = d.toISOString().slice(0, 10);
                  const inMonth = d.getMonth() === base.getMonth();
                  const isToday = iso === todayIso;
                  return (
                    <div key={i} className={`text-[10px] text-center pt-1 pb-0.5 ${inMonth ? 'text-gray-700' : 'text-gray-300'}`}>
                      <span className={isToday ? 'inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#13736D] text-white text-[9px] font-bold' : ''}>
                        {d.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Barres d'interventions */}
              <div className="relative" style={{ minHeight: `${minBodyH}px` }}>
                {bars.map((bar, i) => {
                  const c = colorFor(bar.item);
                  const label = bar.item.works_company || bar.item.description || 'Intervention';
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onBarClick(bar.item)}
                      title={`${label}${bar.item.works_phase ? ' · ' + bar.item.works_phase : ''}`}
                      className={`absolute ${c.bar} text-white text-[9px] truncate px-1 rounded-sm hover:brightness-110 hover:shadow transition cursor-pointer text-left font-medium`}
                      style={{
                        left: `calc(${(bar.startCol / 7) * 100}% + 1px)`,
                        width: `calc(${(bar.span / 7) * 100}% - 2px)`,
                        top: `${bar.row * ROW_H + 1}px`,
                        height: `${ROW_H - 2}px`,
                        lineHeight: `${ROW_H - 2}px`,
                      }}
                      data-testid={`side-cal-bar-${bar.item.item_id}-w${wIdx}`}
                    >
                      {bar.startsHere ? label : '·'}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[9px] text-gray-400 italic">Cliquez une barre pour aller a l&apos;intervention</p>
    </div>
  );
};


const PHASE_PRESETS = [
  'Demolition',
  'Gros oeuvre',
  'Plomberie',
  'Electricite',
  'Placo',
  'Cloisons',
  'Isolation',
  'Menuiseries',
  'Peinture',
  'Carrelage',
  'Sols',
  'Cuisine',
  'Salle de bain',
  'Finitions',
];

const formatDate = (iso) => {
  if (!iso) return 'Date a definir';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      weekday: 'short', day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const formatEUR = (n) => {
  if (n == null) return '—';
  return (n).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' EUR HT';
};

const EMPTY_DRAFT = {
  description: '',
  target_date: '',
  end_date: '',
  status: 'planifie',
  works_phase: '',
  works_company: '',
  comment: '',
  responsible: '',
};

const WorksScheduleSection = ({ projectId, mode = 'internal', endpoint = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const url = endpoint || (
    mode === 'client'
      ? `${API}/portal/works-schedule`
      : `${API}/projects/${projectId}/works-schedule`
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [url]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId(null);
    setShowAddForm(false);
  };

  const startEdit = (item) => {
    setDraft({
      description: item.description || '',
      target_date: (item.start_date || item.target_date || '').slice(0, 10),
      end_date: (item.end_date || '').slice(0, 10),
      status: item.status || 'planifie',
      works_phase: item.works_phase || '',
      works_company: item.works_company || '',
      comment: item.comment || '',
      responsible: item.responsible || '',
    });
    setEditingId(item.item_id);
    setShowAddForm(true);
  };

  const submit = async () => {
    if (!draft.description.trim()) {
      alert('La description est obligatoire.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        description: draft.description.trim(),
        start_date: draft.target_date || null,
        end_date: draft.end_date || null,
        target_date: draft.target_date || null,
        status: draft.status,
        works_phase: draft.works_phase || null,
        works_company: draft.works_company || null,
        comment: draft.comment || null,
        responsible: draft.responsible || null,
      };
      if (editingId) {
        await axios.put(`${API}/projects/${projectId}/works-schedule/items/${editingId}`, payload);
      } else {
        await axios.post(`${API}/projects/${projectId}/works-schedule/items`, payload);
      }
      await fetchData();
      resetForm();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (itemId) => {
    if (!window.confirm('Supprimer cette intervention du planning ?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/works-schedule/items/${itemId}`);
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  // Auto-save inline d'un champ d'un item existant (status/target_date/works_budget_ht).
  // Met a jour optimistiquement le state puis envoie un PUT en arriere-plan.
  // Iter 88 (Fev 2026) : NE PAS refetch apres chaque patch -- ca causait un re-render
  // complet de la timeline qui faisait sauter la page en haut quand l'utilisateur
  // modifiait une date. L'optimistic update est suffisant pour l'affichage local.
  // Refetch uniquement quand le status change (pour MAJ des KPIs du sommaire).
  const inlinePatch = useCallback(async (itemId, patch) => {
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const nextItems = (prev.items || []).map((it) => {
        if (it.item_id !== itemId) return it;
        const merged = { ...it, ...patch };
        // Sync target_date / start_date pour compat
        if ('start_date' in patch) merged.target_date = patch.start_date;
        return merged;
      });
      return { ...prev, items: nextItems };
    });
    try {
      await axios.put(`${API}/projects/${projectId}/works-schedule/items/${itemId}`, patch);
      // Refetch uniquement si le status change (pour les KPIs du sommaire)
      if ('status' in patch) {
        fetchData();
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur mise a jour');
      fetchData();
    }
  }, [projectId, fetchData]);

  if (loading) {
    return (
      <div className="realease-card p-6" data-testid="works-schedule-loading">
        <p className="text-sm text-gray-400 italic">Chargement du previsionnel travaux...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="realease-card p-6" data-testid="works-schedule-error">
        <p className="text-sm text-red-600">Erreur : {error}</p>
      </div>
    );
  }

  const items = data?.items || [];
  const summary = data?.summary || {};
  const internal = mode === 'internal';

  return (
    <div className="realease-card p-6" data-testid="works-schedule-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-lg">
            <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6h13M5 11V5a2 2 0 012-2h10a2 2 0 012 2v4M5 11h14M3 21h18" />
            </svg>
            {internal ? 'Planning previsionnel' : 'Previsionnel travaux'}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {internal
              ? 'Genere depuis les intervenants du budget (dates modifiables ici) + jalons libres.'
              : 'Calendrier previsionnel des travaux de votre projet.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && summary.start_date && summary.end_date && (
            <div className="text-right text-xs text-gray-500" data-testid="works-schedule-dates">
              Du <b>{formatDate(summary.start_date)}</b><br />au <b>{formatDate(summary.end_date)}</b>
            </div>
          )}
          {internal && !showAddForm && (
            <button
              type="button"
              onClick={() => { setDraft(EMPTY_DRAFT); setEditingId(null); setShowAddForm(true); }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] transition"
              data-testid="works-schedule-add-btn"
            >
              + Intervention
            </button>
          )}
        </div>
      </div>

      {/* Formulaire d'ajout / edition */}
      {internal && showAddForm && (
        <div className="mb-6 p-4 bg-[#13736D]/5 border border-[#13736D]/20 rounded-lg" data-testid="works-schedule-form">
          <h4 className="text-sm font-semibold text-[#13736D] mb-3">
            {editingId ? 'Modifier l\'intervention' : 'Ajouter une intervention au planning'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Description *</label>
              <input
                type="text"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Ex: Demolition cloison salon"
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-description"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Date debut</label>
              <input
                type="date"
                value={draft.target_date}
                onChange={(e) => setDraft({ ...draft, target_date: e.target.value })}
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-date"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Date fin</label>
              <input
                type="date"
                value={draft.end_date || ''}
                onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-end-date"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Statut</label>
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-status"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Corps d'état (plusieurs possibles)</label>
              <input
                type="text"
                value={draft.works_phase}
                onChange={(e) => setDraft({ ...draft, works_phase: e.target.value })}
                placeholder="Ex : Plomberie, Électricité, Placo…"
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-phase"
              />
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="works-schedule-phase-chips">
                {PHASE_PRESETS.map((ph) => {
                  const selected = (draft.works_phase || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                  const active = selected.includes(ph.toLowerCase());
                  return (
                    <button
                      key={ph}
                      type="button"
                      onClick={() => {
                        const parts = (draft.works_phase || '').split(',').map(s => s.trim()).filter(Boolean);
                        const next = active
                          ? parts.filter(p => p.toLowerCase() !== ph.toLowerCase())
                          : [...parts, ph];
                        setDraft({ ...draft, works_phase: next.join(', ') });
                      }}
                      className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                        active
                          ? 'bg-[#13736D] text-white border-[#13736D]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#13736D] hover:text-[#13736D]'
                      }`}
                      data-testid={`works-schedule-phase-chip-${ph.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {active ? '✓ ' : ''}{ph}
                    </button>
                  );
                })}
              </div>
              <p className="text-[9px] text-gray-400 mt-1">Cliquez plusieurs corps d'état pour les cumuler sur la même phase.</p>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Entreprise / Artisan</label>
              <input
                type="text"
                value={draft.works_company}
                onChange={(e) => setDraft({ ...draft, works_company: e.target.value })}
                placeholder="Nom de l'intervenant"
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-company"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Responsable interne</label>
              <input
                type="text"
                value={draft.responsible}
                onChange={(e) => setDraft({ ...draft, responsible: e.target.value })}
                placeholder="Ex: Tony, Charles..."
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-responsible"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Commentaire</label>
              <textarea
                value={draft.comment}
                onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                rows={2}
                className="realease-input mt-1 text-sm"
                data-testid="works-schedule-input-comment"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
              data-testid="works-schedule-save-btn"
            >
              {saving ? 'Enregistrement...' : (editingId ? 'Mettre a jour' : 'Ajouter')}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
              data-testid="works-schedule-cancel-btn"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Stats + progress bar */}
      {items.length > 0 && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-2 text-center" data-testid="works-schedule-stats">
          <div className="p-2 rounded-lg bg-gray-50">
            <p className="text-2xl font-bold text-gray-800">{summary.total}</p>
            <p className="text-[10px] uppercase text-gray-500 tracking-wide">Etapes</p>
          </div>
          <div className="p-2 rounded-lg bg-green-50">
            <p className="text-2xl font-bold text-green-700">{summary.done}</p>
            <p className="text-[10px] uppercase text-green-700 tracking-wide">Terminees</p>
          </div>
          <div className="p-2 rounded-lg bg-amber-50">
            <p className="text-2xl font-bold text-amber-700">{summary.in_progress}</p>
            <p className="text-[10px] uppercase text-amber-700 tracking-wide">En cours</p>
          </div>
          {internal ? (
            <div className="p-2 rounded-lg bg-gray-50">
              <p className="text-2xl font-bold text-gray-800">{summary.planned}</p>
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">A venir</p>
            </div>
          ) : (
            <div className="p-2 rounded-lg bg-gray-50">
              <p className="text-2xl font-bold text-gray-800">{summary.planned}</p>
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">A venir</p>
            </div>
          )}
        </div>
      )}

      {/* Iter 92 : Layout 2 colonnes -- timeline a gauche, calendrier Google-Agenda style a droite */}
      <div className="lg:flex lg:gap-4">
        <div className="lg:flex-1 min-w-0">
      {items.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Avancement global</span>
            <span className="font-semibold text-[#13736D]">{summary.progress_pct}%</span>
            <span className="text-[10px]">{summary.progress_pct >= 100 ? '✓' : ''}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#13736D] to-[#008A8D] transition-all"
              style={{ width: `${summary.progress_pct || 0}%` }}
              data-testid="works-schedule-progress"
            />
          </div>
        </div>
      )}

      {/* Timeline verticale */}
      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400 italic" data-testid="works-schedule-empty">
          {internal
            ? 'Aucune intervention. Cliquez sur "+ Intervention" pour en ajouter une, ou cochez "Inclure dans le previsionnel travaux" sur un delai.'
            : 'Le previsionnel travaux sera mis a jour des que les premieres etapes seront planifiees.'}
        </div>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-3 space-y-1.5" data-testid="works-schedule-timeline">
          {items.map((it) => {
            // Iter 90 (Fev 2026) : detection "demarre aujourd'hui" pour badge pulse
            const todayIso = new Date().toISOString().slice(0, 10);
            const itemStartIso = (it.start_date || it.target_date || '').slice(0, 10);
            const startsToday = itemStartIso === todayIso && it.status !== 'termine';
            // Iter 86 (Fev 2026) : si l'item est termine ou en_cours, prioriser le STATUS
            // pour afficher "Termine"/"En cours" au lieu de "A venir" (ancien alert color).
            let meta;
            if (internal) {
              const s = it.status || 'planifie';
              if (s === 'termine' || s === 'en_cours') {
                meta = STATUS_META[s];
              } else {
                meta = ALERT_META[it.alert] || ALERT_META.gray;
              }
            } else {
              meta = STATUS_META[it.client_status] || STATUS_META.planifie;
            }
            const isCustom = it.source === 'custom';
            return (
              <li key={it.item_id} className="ml-5" data-testid={`works-schedule-item-${it.item_id}`}>
                <span className={`absolute -left-2 flex items-center justify-center w-3 h-3 rounded-full ${meta.dot} ring-2 ring-white`} />
                <div className={`px-3 py-1.5 rounded-lg ${meta.bg} border border-gray-100`}>
                  {/* Iter 93 : ligne unique compacte -- titre + badges a gauche, editeurs (dates/statut/cout/actions) a droite */}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {/* GAUCHE : badges + titre */}
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      {it.works_phase && it.works_phase.split(',').map(s => s.trim()).filter(Boolean).map((ph, pi) => (
                        <span key={pi} className="text-[9px] font-bold uppercase tracking-wider text-[#13736D] bg-white px-1.5 py-0.5 rounded flex-shrink-0">
                          {ph}
                        </span>
                      ))}
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${meta.text} bg-white flex-shrink-0`}>
                        {meta.label}
                      </span>
                      {startsToday && (
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white animate-pulse flex-shrink-0"
                          title="Cette intervention demarre aujourd'hui"
                          data-testid={`works-schedule-starts-today-${it.item_id}`}
                        >
                          DEMARRE AUJOURD&apos;HUI
                        </span>
                      )}
                      <span className="text-sm font-semibold text-gray-900 truncate">{it.works_company || it.description}</span>
                    </div>
                    {/* DROITE : editeurs inline + cout + actions (visible uniquement en mode internal) */}
                    {internal ? (
                      <div className="flex items-center gap-1.5 flex-shrink-0" data-testid={`works-schedule-inline-${it.item_id}`}>
                        <input
                          type="date"
                          key={`start-${it.item_id}`}
                          defaultValue={(it.start_date || it.target_date || '').slice(0, 10)}
                          onBlur={(e) => {
                            const v = e.target.value || null;
                            const current = (it.start_date || it.target_date || '').slice(0, 10);
                            if ((v || '') !== current) inlinePatch(it.item_id, { start_date: v });
                          }}
                          title="Date de debut"
                          className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] bg-white"
                          data-testid={`works-schedule-inline-date-${it.item_id}`}
                        />
                        <span className="text-[9px] text-gray-400">→</span>
                        <input
                          type="date"
                          key={`end-${it.item_id}`}
                          defaultValue={(it.end_date || '').slice(0, 10)}
                          onBlur={(e) => {
                            const v = e.target.value || null;
                            const current = (it.end_date || '').slice(0, 10);
                            if ((v || '') !== current) inlinePatch(it.item_id, { end_date: v });
                          }}
                          title="Date de fin"
                          className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] bg-white"
                          data-testid={`works-schedule-inline-end-${it.item_id}`}
                        />
                        <select
                          value={it.status || 'planifie'}
                          onChange={(e) => inlinePatch(it.item_id, { status: e.target.value })}
                          className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] bg-white font-semibold"
                          data-testid={`works-schedule-inline-status-${it.item_id}`}
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {/* Cout retire du planning (visible uniquement dans le budget previsionnel) */}
                        {isCustom && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(it)}
                              className="p-1 rounded text-gray-400 hover:bg-white hover:text-[#13736D] transition"
                              title="Modifier"
                              data-testid={`works-schedule-edit-${it.item_id}`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => remove(it.item_id)}
                              className="p-1 rounded text-gray-400 hover:bg-white hover:text-red-600 transition"
                              title="Supprimer"
                              data-testid={`works-schedule-delete-${it.item_id}`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 flex-shrink-0">
                        <span>
                          {it.end_date && it.start_date ? `Du ${formatDate(it.start_date)} au ${formatDate(it.end_date)}` : formatDate(it.start_date || it.target_date)}
                        </span>
                        {it.works_company && <span>· {it.works_company}</span>}
                      </div>
                    )}
                  </div>
                  {/* Sous-ligne meta (description/responsable/commentaire) uniquement si non vide */}
                  {internal && (it.description || it.responsible || it.comment) && (
                    <p className="text-[11px] text-gray-600 mt-0.5 leading-snug truncate" data-testid={`works-schedule-meta-${it.item_id}`}>
                      {it.description && <span className="font-medium">{it.description}</span>}
                      {it.responsible && <span> · Prise en charge <b>{it.responsible}</b></span>}
                      {it.comment && <span className="italic text-gray-500"> · &laquo;{it.comment}&raquo;</span>}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
        </div>
        {/* Iter 92 : Sidebar calendrier (lg+) */}
        {items.length > 0 && internal && (
          <aside className="lg:w-80 lg:flex-shrink-0 mt-4 lg:mt-0" data-testid="works-schedule-sidebar">
            <div className="lg:sticky lg:top-4">
              <ScheduleSideCalendar items={items} />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default WorksScheduleSection;
